/**
 * Consumption under a cap (APP-ENTITLEMENT-001, Q-AE-9): a consumable is INCLUDED up to a quantity per period,
 * then sold in whole blocks up to a cap the store set — and when the cap is reached the request is REFUSED, never
 * billed. There is no overage anywhere.
 *
 * @module @puffin-core/app-catalog/rules/caps
 */

'use strict';

const { APP_CATALOG_REFUSAL, AppCatalogRefusal } = require('../refusal');

function assertCount(value, name, { allowNull = false } = {}) {
  if (allowNull && value === null) return;
  if (!Number.isInteger(value) || value < 0) {
    throw new TypeError(`${name} must be a non-negative integer, got ${JSON.stringify(value)}`);
  }
}

/**
 * The period's ceiling: the inclusion plus every block the store allowed. `capBlocks === null` means the store set
 * no blocks — the inclusion is the ceiling.
 *
 * @param {{includedQuantity: number, blockSize: number, capBlocks: ?number}} params
 * @returns {number}
 */
function periodCeiling({ includedQuantity, blockSize, capBlocks }) {
  assertCount(includedQuantity, 'includedQuantity');
  if (!Number.isInteger(blockSize) || blockSize <= 0) {
    throw new TypeError(`blockSize must be a positive integer, got ${JSON.stringify(blockSize)}`);
  }
  assertCount(capBlocks, 'capBlocks', { allowNull: true });
  return includedQuantity + (capBlocks === null ? 0 : capBlocks) * blockSize;
}

/**
 * Whole blocks consumed beyond the inclusion — what the period close invoices.
 *
 * @param {{includedQuantity: number, blockSize: number, consumed: number}} params
 * @returns {number}
 */
function blocksConsumed({ includedQuantity, blockSize, consumed }) {
  assertCount(consumed, 'consumed');
  const beyond = consumed - includedQuantity;
  return beyond <= 0 ? 0 : Math.ceil(beyond / blockSize);
}

/**
 * Judge a request to consume `requested` more units when `consumed` are already used this period.
 *
 * @param {Object} params
 * @param {number} params.includedQuantity
 * @param {number} params.blockSize
 * @param {?number} params.capBlocks - blocks the store allowed beyond the inclusion; null = none.
 * @param {number} params.consumed - units used so far this period.
 * @param {number} [params.requested=1] - units this request would use.
 * @returns {{allowed: boolean, ceiling: number, remaining: number, blocksBilled: number, refusal: ?string}}
 *   `refusal` is `CAP_REACHED` when the request would pass the ceiling; `blocksBilled` is the whole-block count
 *   the period would invoice AFTER this request, when allowed.
 */
function judgeConsumption({ includedQuantity, blockSize, capBlocks, consumed, requested = 1 }) {
  const ceiling = periodCeiling({ includedQuantity, blockSize, capBlocks });
  assertCount(consumed, 'consumed');
  if (!Number.isInteger(requested) || requested <= 0) {
    throw new TypeError(`requested must be a positive integer, got ${JSON.stringify(requested)}`);
  }
  const after = consumed + requested;
  if (after > ceiling) {
    return {
      allowed: false,
      ceiling,
      remaining: Math.max(0, ceiling - consumed),
      blocksBilled: blocksConsumed({ includedQuantity, blockSize, consumed }),
      refusal: APP_CATALOG_REFUSAL.CAP_REACHED,
    };
  }
  return {
    allowed: true,
    ceiling,
    remaining: ceiling - after,
    blocksBilled: blocksConsumed({ includedQuantity, blockSize, consumed: after }),
    refusal: null,
  };
}

/**
 * A store may lower its cap, but never below what the period has already consumed beyond the inclusion — a cap
 * under consumption would invoice blocks the cap forbids.
 *
 * @param {{includedQuantity: number, blockSize: number, consumed: number, capBlocks: ?number}} params
 * @returns {?number} the cap, validated.
 * @throws {AppCatalogRefusal} `CAP_BELOW_CONSUMED`.
 */
function assertCapAllowsConsumed({ includedQuantity, blockSize, consumed, capBlocks }) {
  const used = blocksConsumed({ includedQuantity, blockSize, consumed });
  assertCount(capBlocks, 'capBlocks', { allowNull: true });
  if ((capBlocks === null ? 0 : capBlocks) < used) {
    throw new AppCatalogRefusal(APP_CATALOG_REFUSAL.CAP_BELOW_CONSUMED, {
      capBlocks,
      blocksConsumed: used,
    });
  }
  return capBlocks;
}

module.exports = { periodCeiling, blocksConsumed, judgeConsumption, assertCapAllowsConsumed };
